/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import {CRUDPermissions, GenericObject} from '@ion/core/src/types/entities';
import {AssetTreeRefresh} from '@ion/dls/src/editors/AssetEditor';
import {AuditDateFilterProps} from '@ion/dls/src/editors/audit/AuditDialog';
import {DeviceEditorOptions} from '@ion/dls/src/editors/DeviceEditor';
import {SelectAllSearch} from '@ion/features/src/search/types';
import {AssetDeviceDialog} from './AssetDeviceDialog';
import AuditDeviceDialog, {AuditDeviceDialogProps} from './AuditDeviceDialog';
import BulkViewDevicesDialog from './BulkViewDevicesDialog';
import EditDeviceDialog, {EditDeviceDialogProps} from './EditDeviceDialog';
import {JobDeviceDialog, JobDeviceDialogProps} from './JobDeviceDialog';
import MoveDeviceDialog from './MoveDeviceDialog';
import {SwapDeviceDialog} from './SwapDeviceDialog';
import ViewDeviceAlertDialogs from './ViewDeviceAlertDialogs';
import ViewDeviceDialog, {ViewDeviceOptions} from './ViewDeviceDialog';
import ViewDeviceEventDialog from './ViewDeviceEventDialog';

export interface JobSearchEngine {
  jobSearchEngineEnabled?: boolean;
}

export interface DeviceActionDialogsProps
  extends SelectAllSearch,
  ViewDeviceOptions,
  AuditDeviceDialogProps,
  AuditDateFilterProps,
  DeviceEditorOptions,
  EditDeviceDialogProps,
  JobDeviceDialogProps,
  JobSearchEngine,
  AssetTreeRefresh {
  includeAssetAssociation?: boolean;
  includeGroups?: boolean;
  includeSwapDevice?: boolean;
  includeSpreaderChooser?: boolean;
  isMultiSelectDevice?: boolean;
  isMultiSelectSpreader?: boolean;
  includeGroupProperties?: boolean;
  includeDeviceGridCheckboxes?: boolean;
  includeAssetGridCheckboxes?: boolean;
  enableAlertDialogs?: boolean;
  enableEventDialogs?: boolean;
  assetsPermissions?: CRUDPermissions;
  isShowRefreshButton?: boolean;
  devicesPermissions?: CRUDPermissions;
  hasCreateJob?: boolean;
  isCtcAdmin?: boolean;
  Features?: GenericObject;
  selectedJobId?: (jobId: number) => void;
  isVbuFwEnabled?: boolean;
}

/**
 * Hosts all required re-usable dialogs for managing a device.
 * Does not include bulk dialogs.
 */
export const DeviceActionDialogs = ({
  filters,
  search,
  selectedJobId,
  assetsPermissions,
  devicesPermissions,
  hasCreateJob,
  Features,
  includeGroups = false,
  areTotalRowsSelected = false,
  totalRowsCount = 0,
  includeGroupProperties = true,
  includeSubAccounts = false,
  includeGPSFields = false,
  includeAssetAssociation = true,
  includeSwapDevice = true,
  includeSpreaderChooser = false,
  isMultiSelectSpreader = false,
  includeDeviceGridCheckboxes = false,
  includeAssetGridCheckboxes = false,
  disableAccountChooser = true,
  enableAuditDialog = true,
  enableAdvancedViewDialog = true,
  enableAlertDialogs = true,
  enableEventDialogs = true,
  enableGridMenuRows = true,
  enableJobDialog = true,
  enableViewDialog = true,
  explicitReportDate = true,
  includeAdditionalDeviceFields = true,
  isDeviceNotes = false,
  includeDatetimeInformation = true,
  includeEditActionInViewDialog = true,
  refreshDevice = true,
  showDifferences = true,
  enableDateFilter = false,
  showSCISettings = true,
  usePuls = true,
  showDeviceType = true,
  isToggleSwitchGrid = false,
  isVbuFwEnabled = false,
  isShowColumnChooserIcon = false,
  isShowRefreshButton = true,
  isCtcAdmin = false,
  showTreeRefresh = false,
  isMultiSelectDevice = false,
  jobSearchEngineEnabled = false
}: DeviceActionDialogsProps) => (
  <>
    <ViewDeviceDialog refreshDevice={refreshDevice}
      Features={Features}
      selectedJobId={selectedJobId}
      includeEditAction={includeEditActionInViewDialog}
      enableViewDialog={enableViewDialog}
      isVbuFwEnabled={isVbuFwEnabled}
      explicitReportDate={explicitReportDate}
      isToggleSwitchGrid={isToggleSwitchGrid}
      isShowColumnChooserIcon={isShowColumnChooserIcon}
      hasCreateJob={hasCreateJob}
      devicesPermissions={devicesPermissions}
      isCtcAdmin={isCtcAdmin}
      isShowRefreshButton={isShowRefreshButton} />
    <MoveDeviceDialog />
    <AuditDeviceDialog enableAuditDialog={enableAuditDialog}
      showDifferences={showDifferences}
      showAuditVersion={isCtcAdmin}
      enableDateFilter={enableDateFilter}
      enableGridMenuRows={enableGridMenuRows}/>
    <AssetDeviceDialog includeAssetAssociation={includeAssetAssociation}
      showDifferences={showDifferences}
      isMultiSelectDevice={isMultiSelectDevice}
      includeDeviceGridCheckboxes={includeDeviceGridCheckboxes}
      includeGroupProperties={includeGroupProperties}
      assetsPermissions={assetsPermissions}
      includeAssetGridCheckboxes={includeAssetGridCheckboxes}
      showTreeRefresh={showTreeRefresh} />
    <EditDeviceDialog usePuls={usePuls}
      includeGroupProperties={includeGroupProperties}
      enableAdvancedViewDialog={enableAdvancedViewDialog}
      includeAdditionalDeviceFields={includeAdditionalDeviceFields}
      isDeviceNotes={isDeviceNotes}
      includeGPSFields={includeGPSFields}
      includeDatetimeInformation={includeDatetimeInformation}
      includeSpreaderChooser={includeSpreaderChooser}
      isMultiSelectSpreader={isMultiSelectSpreader}
      includeGroups={includeGroups}
      showSCISettings={showSCISettings}
      showDeviceType={showDeviceType}
      disableAccountChooser={disableAccountChooser} />
    <JobDeviceDialog includeSubAccounts={includeSubAccounts}
      jobSearchEngineEnabled={jobSearchEngineEnabled}
      enableJobDialog={enableJobDialog}
      Features={Features}
      disableAccountChooser={disableAccountChooser}
      areTotalRowsSelected={areTotalRowsSelected}
      totalRowsCount={totalRowsCount}
      search={search}
      filters={filters} />
    <SwapDeviceDialog includeSwapDevice={includeSwapDevice}
      includeGroupProperties={includeGroupProperties}
      showSCISettings={showSCISettings}
      disableDeviceAccountChooser={disableAccountChooser} />
    <ViewDeviceEventDialog enabled={enableEventDialogs} />
    <ViewDeviceAlertDialogs enabled={enableAlertDialogs} />
    <BulkViewDevicesDialog refreshDevice={refreshDevice}
      Features={Features}
      selectedJobId={selectedJobId}
      includeEditAction={includeEditActionInViewDialog}
      enableViewDialog={enableViewDialog}
      explicitReportDate={explicitReportDate}
      isVbuFwEnabled={isVbuFwEnabled}
      isToggleSwitchGrid={isToggleSwitchGrid}
      isShowColumnChooserIcon={isShowColumnChooserIcon}
      hasCreateJob={hasCreateJob}
      devicesPermissions={devicesPermissions}
      isCtcAdmin={isCtcAdmin}
      isShowRefreshButton={isShowRefreshButton} />
  </>
);

export default DeviceActionDialogs;
